import ReactModal from 'react-modal';
import './AccountModal.scss';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { useSelector } from 'react-redux';
import { selectControls } from '../../../redux/controls';
import React, { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ModalProps } from '../models/Modal.interfaces';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import Spinner from '../../Spinner/Spinner';
import { processCreateAccount } from '../models';

function AccountModal({ open, onClose }: ModalProps): JSX.Element {

  const controls = useSelector(selectControls);

  ReactModal.setAppElement('#root');

  const formEl = useRef<HTMLFormElement>(null);

  const [errMsg, setErrMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successful, setSuccessful] = useState<boolean>(false);

  const [contentLabel, setContentLabel] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [pwMismatch, setPWMismatch] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const submitFormHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      if (
        formProps['password'] !== formProps['confirm-password']
      ) {
        setPWMismatch(true);
        const pwEl = document.getElementById('acc-modal-pw') as HTMLInputElement;
        if (pwEl) {
          pwEl.focus();
          pwEl.select();
        }
      } else {
        setPWMismatch(false);
        processCreateAccount(
          {
            emailId: formProps['email'] as string,
            password: formProps['password'] as string
          },
          setIsLoading,
          setSuccessful,
          setErrMsg
        );
        setSubmitted(true);
        setProcessing(true);
      }
    }
  }, []);

  const closeModalHandler = useCallback(() => {
    setSubmitted(false);
    setProcessing(false);
    setPWMismatch(false);
    setIsValid(false);
    onClose(true);
    
  }, [onClose]);

  const resetPWMismatch = useCallback(() => {
    setPWMismatch(false);
  },[]);

  // fires on mount - sets headerText and bodyText based on windowType
  useEffect(() => {
    if (controls) {
      setContentLabel(controls?.CreateHeader?.controlText);
    }
  }, [controls]);

  useEffect(() => {
    if (!isLoading && processing) {
      setProcessing(false);
    }
  }, [isLoading, processing]);


  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          // onAfterOpen={}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={contentLabel}
        >
          {
            !submitted || processing  ?
              <>
                <form ref={formEl} onSubmit={(e) => submitFormHandler(e, formEl)} className='nqt-modal--container'>
                  { isLoading && <Spinner/> }
                  <div className="nqt-modal--header-group">
                    <header className='nqt-modal--title'>{contentLabel}</header>
                    <button className='nqt-modal--close-button'
                      type='button'
                      ref={(el) => !isValid && el?.focus()}
                      onClick={() => { onClose(false); resetPWMismatch();} }>
                      <DeleteIcon/>
                      <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                    </button>
                  </div>
                  <div className="nqt-modal--border">
                    <section className="nqt-acc-modal--content container">
                      <span className="row row-cols-1">
                        <span className="col nqt-acc-modal--description">
                          {
                            controls?.CreateInstruct?.controlText
                          }
                        </span>
                      </span>
                      <hr className="row"/>
                      <div className="nqt-modal--form-controls container-fluid">
                        <div className="row row-cols-1 gy-3">
                          {
                            pwMismatch &&
                        <div className="col nqt-modal--error">
                          <span className="nqt-modal--error-message">
                            {
                              controls?.PasswordMustMatch?.controlText
                            }
                          </span>
                        </div>
                          }
                          <div className="col container-fluid">
                            <TextFormControl id='acc-modal-email'
                              autoComplete="email"
                              containerClass="nqt-modal--text-input"
                              controlName='email'
                              controlType="email"
                              errorText={controls?.LoginValidEmail?.controlText}
                              headerText={controls?.LoginID?.controlText}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{1,10}$"
                              required={true}
                              setFocus={false}/>
                          </div>
                          <div className="col container-fluid">
                            <TextFormControl id='acc-modal-pw'
                              autoComplete="new-password"
                              containerClass="nqt-modal--text-input password"
                              controlName='password'
                              controlType="password"
                              errorText={
                                controls?.PasswordInstruct ? controls?.PasswordInstruct?.controlText : ''
                              }
                              headerText={controls?.LoginPwd?.controlText}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true}/>
                          </div>
                          <div className="col container-fluid">
                            <TextFormControl id='acc-modal-confirm-pw'
                              autoComplete="new-password"
                              containerClass="nqt-modal--text-input"
                              controlName='confirm-password'
                              controlType="password"
                              headerText={controls?.LoginPwd2?.controlText}
                              isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true} />
                          </div>
                        </div>
                      </div>
                    </section>
                    <hr />
                    <div className="nqt-modal--buttons col-9 offset-3">
                      <button className="nqt-button--cancel col-4 offset-1"
                        type='button'
                        onClick={() => onClose(false)}>{controls?.ModalCancel?.controlText}</button>
                      <button className="nqt-button--submit col-6"
                        disabled={!isValid}
                        type="submit"
                        onClick={(e) => { submitFormHandler(e, formEl);}}>{controls?.Create?.controlText}</button>
                    </div>
                  </div>
                </form>
              </> :
              <div className='nqt-modal--container'>
                <div className="nqt-modal--header-group">
                  <header className='nqt-modal--title'>{contentLabel}</header>
                  <button className='nqt-modal--close-button'
                    ref={(el) => el?.focus()}
                    onClick={() => closeModalHandler()}>
                    <DeleteIcon/>
                    <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                  </button>
                </div>
                <div className="nqt-modal--border">
                  <section className="nqt-modal--content">
                    {
                      successful ?
                        <div className="nqt-modal--description">
                          {
                            controls?.CreateSuccess?.controlText
                          }
                        </div> :
                        <div className="nqt-modal--description">
                          {
                            errMsg ?
                            errMsg as string :
                              controls?.StandardErrorMsg?.controlText
                          }
                        </div>
                    }
                  </section>
                </div>
              </div>
          }
        </ReactModal>
      }
    </>
  );
}

export default AccountModal;